import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import type { StepperGraph, StepperStep } from "./types";

/** One step as the position endpoint sends it — an ordered array, not a map. */
interface PositionStep extends Omit<StepperStep, "channel"> {
  sequence: number;
  channel?: string;
}

interface PositionResponse {
  adventureId: number | string;
  currentStep: string;
  resumed: boolean;
  steps: PositionStep[];
}

/**
 * Load the web half of the adventure, and where this visitor is in it.
 *
 * Throws rather than falling back to a bundled copy: a stale snapshot would
 * render a signup that looks right and is wrong. Offline runs are served by
 * MSW at the same URL.
 *
 * The server sends WEB-channel steps only and resolves `currentStep` from the
 * visitor's journey, so `firstStep` is where they left off, not always the
 * opening screen.
 */
export async function loadStepperAdventure(
  id: string | number,
): Promise<StepperGraph> {
  const adventureId = Number(id);
  if (!Number.isInteger(adventureId) || adventureId <= 0) {
    throw new Error(
      `[stepper] ${JSON.stringify(id)} is not an adventure id. ` +
        `Set VITE_STEPPER_ADVENTURE_ID, or pass the id from the route.`,
    );
  }

  const { data } = await apiClient.get(
    API_ENDPOINTS.stepper.position(adventureId),
  );
  const result = (data?.result ?? data) as PositionResponse;

  // No steps means a misconfiguration, not an empty flow — usually the wrong
  // id, or an adventure whose steps are all on the SMS channel. Saying so beats
  // a blank screen and a hunt through the network tab.
  if (!result?.steps?.length) {
    throw new Error(`[stepper] adventure ${adventureId} returned no web steps`);
  }

  const steps: Record<string, StepperStep> = {};
  for (const step of result.steps) {
    steps[step.stepName] = { ...step, channel: step.channel ?? "WEB" };
  }

  return {
    adventureId: result.adventureId ?? adventureId,
    firstStep: result.currentStep,
    resumed: Boolean(result.resumed),
    steps,
  };
}
